import {supplementRegexBaseString} from 'client/generic/MarkdownRendererV2/rules/supplementRules';
import supplementStore from 'client/Supplement/SupplementStore';
import {SUPPLEMENT_TYPES} from 'client/Supplements/shared';
import {isEqual} from 'lodash';
import {useFluxStore} from 'client/framework';

type SupplementModel = ReturnType<typeof supplementStore.getSupplement>;

const SUPPLEMENT_TOKENS = new RegExp(supplementRegexBaseString, 'g');

const GENERIC_SUPPLEMENT_NAMES: Record<string, string> = {
  [SUPPLEMENT_TYPES.IMAGE]: 'Image',
  [SUPPLEMENT_TYPES.AUDIO]: 'Audio',
  [SUPPLEMENT_TYPES.PASSAGE]: 'Passage',
  [SUPPLEMENT_TYPES.SORTABLE_TABLE]: 'Table'
};

/**
 * What a screen reader should call a supplement: an image's alt text, an audio clip's caption,
 * otherwise its name, with a generic fallback so it is never nameless.
 */
export const supplementAccessibleName = (supplement: SupplementModel): string => {
  const type = supplement.getType?.();
  const name = supplement.getName?.() || GENERIC_SUPPLEMENT_NAMES[type] || 'Supplement';
  if (type === SUPPLEMENT_TYPES.IMAGE) return supplement.getAlttext?.() || name;
  if (type === SUPPLEMENT_TYPES.AUDIO) return supplement.getCaption?.() || name;
  return name;
};

/**
 * Reduces inline markdown to the words a screen reader should speak: emphasis markers, code
 * ticks, and link syntax go, the text stays. Markers only count where markdown would read them
 * as markup (emphasis bounded by whitespace, links with a URL), so `5*4*3` keeps its asterisks.
 */
export const stripInlineMarkdown = (text: string): string =>
  text
    .replace(/(^|\s)(\*\*|__)(\S(?:.*?\S)?)\2(?=[\s.,;:!?]|$)/g, '$1$3')
    .replace(/(^|\s)([*_])(\S(?:.*?\S)?)\2(?=[\s.,;:!?]|$)/g, '$1$3')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\((?:https?:\/\/|\/|#|mailto:)[^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

const supplementNameFor = (id: string): string =>
  supplementStore.hasSupplement(id)
    ? supplementAccessibleName(supplementStore.getSupplement(id))
    : 'Supplement';

/**
 * What a screen reader should call an item or slot value: its text, or for a supplement its alt
 * text (image), caption (audio), or name, never a raw `[s:…]` token. Reads the supplement store
 * once; in render use `useItemAccessibleName` so the name follows the supplement as it loads.
 */
export const getItemAccessibleName = (value: string): string => {
  const segments: string[] = [];
  let last = 0;
  for (const match of value.matchAll(SUPPLEMENT_TOKENS)) {
    segments.push(value.slice(last, match.index), supplementNameFor(match[1]));
    last = match.index + match[0].length;
  }
  segments.push(value.slice(last));
  return segments.map(stripInlineMarkdown).filter(Boolean).join(', ');
};

/** Render-time `getItemAccessibleName`, subscribed to the supplement store. Empty for no value. */
export const useItemAccessibleName = (value: string | null | undefined): string =>
  useFluxStore(supplementStore, () => (value ? getItemAccessibleName(value) : ''));

/** `useItemAccessibleName` for a list: one subscription, a name per value, in order. */
export const useItemAccessibleNames = (values: string[]): string[] =>
  useFluxStore(supplementStore, () => values.map(getItemAccessibleName), isEqual);
